import { Button, ICON_COLOR } from '@zep-us/zus';
import { ChartLineIcon, LockIcon, RectanglePortraitStackFillIcon, SquareStackIcon } from '@zep-us/zus/icons';
import { useTranslation } from 'next-i18next';
import { NewChip } from '@/shared/components/Chip/NewChip';
import { CTAButtonRightSlot } from './CTAButtonRightSlot';
import { WorksheetGradientIcon } from './WorksheetGradientIcon';

/**
 * ViewerLockedCTAButtons Component
 *
 * @description
 * Renders a read-only locked message for viewers when they view a locked quiz.
 * This component displays an informational message explaining why the quiz is restricted.
 *
 * Story 2.6: Viewer-locked scenario
 *
 * @example
 * ```tsx
 * <ViewerLockedCTAButtons t={t} />
 * ```
 */

export interface ViewerLockedCTAButtonsProps {
  onCopy: () => void;
  /** 학습 리포트 핸들러(관리자 전용) */
  onGoToLms?: () => void;
  /** 워크시트 내보내기 핸들러 */
  onExportWorksheet: () => void;
  /** 플래시카드 학습 진입 핸들러 */
  onFlashcard: () => void;
}
export const ViewerLockedCTAButtons = ({
  onCopy,
  onGoToLms,
  onExportWorksheet,
  onFlashcard
}: ViewerLockedCTAButtonsProps) => {
  const {
    t
  } = useTranslation(['common']);
  const buttonsStyle = 'group w-full justify-start px-md gap-xs';
  return <div className="flex w-full flex-col gap-xs" data-sentry-component="ViewerLockedCTAButtons" data-sentry-source-file="ViewerLockedCTAButtons.tsx">
      <Button size="md" variant="contained" intent="gradient" onClick={onCopy} leftSlot={<SquareStackIcon size="sm" fill={ICON_COLOR.inverse} />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="ViewerLockedCTAButtons.tsx">
        {t('detail.copyToMyQuiz')}
      </Button>
      <div className="flex flex-col gap-xs pt-sm">
        <p className="text-caption-lg-semibold text-alternative">
          {t('common:detail.quizSolve')}
        </p>
        <Button size="md" variant="outlined" intent="alternative" disabled leftSlot={<LockIcon size="sm" fill={ICON_COLOR.strong} opacity={0.54} />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="ViewerLockedCTAButtons.tsx">
          {t('detail.cta.tutorial')}
        </Button>
        {/* 2. 플래시카드 - Flashcard button */}
        <Button size="md" variant="outlined" intent="neutral" onClick={onFlashcard} leftSlot={<RectanglePortraitStackFillIcon size="sm" className="text-blue-300" />} rightSlot={<CTAButtonRightSlot badge={<NewChip className="bg-blue-50 text-blue-500" />} />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="ViewerLockedCTAButtons.tsx">
          {t('common:flashcard.label')}
        </Button>
        {/* 3. 워크시트 - Worksheet button */}
        <Button size="md" variant="outlined" intent="neutral" onClick={onExportWorksheet} leftSlot={<WorksheetGradientIcon size="sm" />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="ViewerLockedCTAButtons.tsx">
          {t('common:quizWorksheet.label')}
        </Button>
        {/* 학습 리포트 - Alternative button (관리자 전용) */}
        {onGoToLms && <Button size="md" variant="contained" intent="alternative" onClick={onGoToLms} leftSlot={<ChartLineIcon size="sm" fill={ICON_COLOR.strong} />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle}>
            {t('detail.learningReport')}
          </Button>}
      </div>

      <p className="text-caption-lg-semibold text-assistive">
        {t('detail.planExpired.viewerMessage')}
      </p>
    </div>;
};